import { z } from 'zod';
import { isoDate, uuid } from './common';

/** Dashboard widgets (FR-RPT-3). Each widget reads one report endpoint with its config. */
export const WIDGET_TYPES = [
  'my_work',
  'team_workload',
  'sprint_burndown',
  'velocity',
  'cfd',
  'cycle_time',
  'bug_trend',
  'utilisation',
  'release_readiness',
  'qa_metrics',
] as const;
export type WidgetType = (typeof WIDGET_TYPES)[number];

export const widgetSchema = z.object({
  id: z.string().min(1).max(40),
  type: z.enum(WIDGET_TYPES),
  title: z.string().max(80).optional(),
  /** projectKey, sprintId, releaseId, days… depending on the widget. */
  config: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])).default({}),
  width: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(1),
});
export type Widget = z.infer<typeof widgetSchema>;

export const dashboardSchema = z.object({
  name: z.string().min(1).max(80),
  projectKey: z.string().nullable().optional(),
  /** Shared dashboards are visible to project members (or the org when no project). */
  shared: z.boolean().default(false),
  widgets: z.array(widgetSchema).max(24).default([]),
});
export type DashboardInput = z.infer<typeof dashboardSchema>;

export const reportRangeQuery = z.object({
  from: isoDate.optional(),
  to: isoDate.optional(),
  projectKey: z.string().optional(),
  sprintId: uuid.optional(),
  releaseId: uuid.optional(),
  userId: uuid.optional(),
  groupBy: z.enum(['project', 'type', 'activity', 'user']).optional(),
  types: z.string().optional(),
  weeks: z.coerce.number().int().min(1).max(52).optional(),
  count: z.coerce.number().int().min(1).max(20).optional(),
});
export type ReportRange = z.infer<typeof reportRangeQuery>;
